import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, FileText, Eye, EyeOff } from "lucide-react";
import { API } from "./adminConfig";

const FAQTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    order: 0,
    visible: true
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/faqs`);
      setFaqs(response.data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Error loading FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faqs/${editingFaq.id}`, formData);
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(`${API}/admin/faqs`, formData);
        toast.success('FAQ created successfully');
      }
      fetchFAQs();
      resetForm();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Error saving FAQ');
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order,
      visible: faq.visible
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faqs/${id}`);
      toast.success('FAQ deleted successfully');
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Error deleting FAQ');
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'general',
      order: 0,
      visible: true
    });
    setEditingFaq(null);
    setShowAddForm(false);
  };

  const categories = ['general', 'perimenopause', 'symptoms', 'treatment', 'membership', 'platform'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-burgundy font-serif">FAQ Management</h2>
          <p className="text-burgundy/70">Manage frequently asked questions for your public FAQ page</p>
        </div>
        <Button 
          onClick={() => setShowAddForm(true)}
          className="bg-burgundy hover:bg-burgundy/80 text-cream"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <CardTitle className="text-burgundy font-serif">
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Question *</label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({...formData, question: e.target.value})}
                  required
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Answer (HTML supported) *</label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({...formData, answer: e.target.value})}
                  required
                  rows={6}
                  className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:border-gold focus:outline-none resize-none"
                />
                <p className="text-xs text-burgundy/60 mt-1">You can use HTML tags for formatting</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:border-gold"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Order</label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                    className="border-burgundy/30 focus:border-gold"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.visible}
                      onChange={(e) => setFormData({...formData, visible: e.target.checked})}
                      className="rounded border-burgundy/30"
                    />
                    <span className="text-sm text-burgundy">Visible</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-burgundy hover:bg-burgundy/80 text-cream">
                  <Save className="h-4 w-4 mr-2" />
                  {editingFaq ? 'Update FAQ' : 'Create FAQ'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="border-burgundy/30 text-burgundy">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* FAQs List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
        </div>
      ) : faqs.length === 0 ? (
        <Card className="border-burgundy/20">
          <CardContent className="text-center py-12">
            <MessageCircle className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-burgundy/60 mb-2 font-serif">No FAQs Yet</h3>
            <p className="text-burgundy/50 mb-4">Create your first FAQ to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <Card key={faq.id} className="border-burgundy/20 hover:border-burgundy/40 transition-all">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-burgundy/10 text-burgundy text-xs">{faq.category}</Badge>
                      <Badge className="bg-gray-100 text-gray-600 text-xs">Order: {faq.order}</Badge>
                      {!faq.visible && <Badge className="bg-red-100 text-red-600 text-xs">Hidden</Badge>}
                    </div>
                    <h4 className="font-medium text-burgundy mb-2">{faq.question}</h4>
                    <div 
                      className="text-sm text-burgundy/70 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(faq)}
                      className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(faq.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


// ==========================================
// CRM TAB COMPONENT
// ==========================================
// ============================================
// EMAIL AUTOMATION SYSTEM

// Campaign Flow Editor Component

export default FAQTab;
